import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function AdminGamePanel() {
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [firstResult, setFirstResult] = useState("");
  const saveSchedule = async () => {
    const instant = new Date(`${firstResult}:00+05:30`);
    if (!Number.isFinite(instant.getTime()) || instant <= new Date()) {
      setError("Choose a future date and time in IST."); return;
    }
    setBusy("schedule"); setError(""); setMessage("");
    try {
      await api.put("/admin/game/schedule", { first_result_at: instant.toISOString() });
      setMessage("Daily result time saved. All pending scheduled entries use this time.");
      setFirstResult(""); await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const load = useCallback(async () => {
    try { setData((await api.get("/admin/game/rounds")).data); setError(""); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); const timer = setInterval(load, 15000); return () => clearInterval(timer); }, [load]);
  const save = async (symbol) => {
    setBusy(symbol); setError(""); setMessage("");
    try {
      await api.put(`/admin/game/rounds/${symbol}`, { direction: choices[symbol] });
      setMessage(`${symbol} winning direction saved.`); await load();
      setChoices((prev) => { const next = { ...prev }; delete next[symbol]; return next; });
    } catch (e) { await load(); setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  return <section className="space-y-3">
    <h2 className="font-bold text-lg">Daily game outcomes</h2>
    <p className="text-xs text-slate-300">Choose the winning direction for each coin. Matching entries win their tier’s percentage of their balance at settlement; opposite entries lose half that rate. Rules are disclosed to users. Each coin locks after its first entry. Changes are audited.</p>
    <p className="text-xs text-amber-300">Open rounds each day. Unconfigured coins reject entries. Resets at 5:00 AM IST.</p>
    <div className="rounded-xl border border-slate-700 p-3 space-y-2">
      <h3 className="font-semibold">Daily result declaration</h3>
      {data?.schedule && <p className="text-xs text-slate-300">Every day at {data.schedule.time} IST. Next: {new Date(data.schedule.next_result_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST.</p>}
      <label className="text-xs block" htmlFor="first-result">First result date and time (IST)</label>
      <input id="first-result" type="datetime-local" min={new Date(Date.now() + 60000 + 330 * 60000).toISOString().slice(0, 16)} max={new Date(Date.now() + 86400000 + 330 * 60000).toISOString().slice(0, 16)} value={firstResult} onChange={(e) => setFirstResult(e.target.value)} className="w-full rounded bg-[#0B0E14] border border-slate-600 p-2" />
      <p className="text-xs text-slate-400">Choose a future time within 24 hours. It repeats daily until changed. Saving reschedules all pending scheduled entries; completed results stay final.</p>
      <Button disabled={!!busy || !firstResult} onClick={saveSchedule} className="bg-emerald-400 text-black">Save result time</Button>
    </div>
    {error && <p role="alert" className="text-sm text-red-400">{error}</p>}
    {message && <p role="status" className="text-sm text-emerald-400">{message}</p>}
    {!data ? <p className="text-sm">Loading rounds…</p> : <>
      <p className="text-xs text-slate-500">Day: {data.day}</p>
      {data.rounds.map((row) => <div key={row.symbol} className="p-3 rounded-xl border border-slate-700 bg-[#121721] space-y-2">
        <div className="flex justify-between text-sm"><strong>{row.symbol}</strong><span className="text-xs text-slate-400">{row.entries} entries · {row.locked ? "Locked" : row.winning_direction ? "Open" : "Not opened"}</span></div>
        <div className="flex items-center gap-2">
          <select aria-label={`${row.symbol} winning direction`} disabled={row.locked || !!busy} value={choices[row.symbol] ?? row.winning_direction ?? ""} onChange={(e) => setChoices({ ...choices, [row.symbol]: e.target.value })} className="rounded bg-[#0B0E14] border border-slate-600 px-2 h-10 flex-1 text-sm">
            <option value="" disabled>Select winner</option><option value="pull">PULL ▲ wins</option><option value="call">CALL ▼ wins</option>
          </select>
          <Button disabled={row.locked || !!busy || !choices[row.symbol]} onClick={() => save(row.symbol)} className="bg-emerald-400 text-black">{busy === row.symbol ? "Saving…" : "Save"}</Button>
        </div>
      </div>)}
      <h3 className="font-semibold pt-3">Today’s configuration log</h3>
      {data.audit.length === 0 && <p className="text-xs text-slate-500">No changes today.</p>}
      {data.audit.map((row, i) => <p key={i} className="text-[11px] text-slate-400 break-all">{row.round_id} → {row.direction.toUpperCase()} · {new Date(row.created_at).toLocaleString()} · Admin {row.admin_id}</p>)}
    </>}
  </section>;
}
